/**
 * Movie Explore Store
 * Global state management for constellation-based film discovery
 */

import { create } from 'zustand';
import type {
  MovieConstellationNode,
  MovieVibeControls,
  MovieExploreResponse,
  RecommendedMovie,
  SavedMovieConstellation,
  CoherenceLevel,
} from '../types/movie-explore.types';

interface MovieExploreStore {
  // Constellation state
  nodes: MovieConstellationNode[];
  vibe: MovieVibeControls;
  currentConstellationId: string | null;
  savedConstellations: SavedMovieConstellation[];

  // Results state
  exploreId: string | null;
  interpretation: string | null;
  results: RecommendedMovie[];
  suggestedNodes: MovieConstellationNode[];
  coherenceScore: number;

  // UI state
  loading: boolean;
  error: string | null;
  cacheHit: boolean;
  responseTimeMs: number;

  // Actions - Constellation Management
  addNode: (node: MovieConstellationNode) => void;
  removeNode: (index: number) => void;
  updateNodeWeight: (index: number, weight: number) => void;
  setVibe: (vibe: Partial<MovieVibeControls>) => void;
  clearConstellation: () => void;
  loadConstellation: (constellation: SavedMovieConstellation) => void;

  // Actions - Saved Constellations
  setSavedConstellations: (constellations: SavedMovieConstellation[]) => void;
  addSavedConstellation: (constellation: SavedMovieConstellation) => void;
  deleteSavedConstellation: (id: string) => void;

  // Actions - Results
  setExploreResults: (response: MovieExploreResponse) => void;
  clearResults: () => void;

  // Actions - UI
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Computed
  isValid: () => boolean;
  getCoherenceLevel: () => CoherenceLevel;
}

/**
 * Default vibe controls (neutral/balanced)
 */
const DEFAULT_VIBE: MovieVibeControls = {
  pacing: 0.5,
  tone: 0.5,
  obscurity: 0.5,
  formalism: 0.5,
};

/**
 * Movie Explore store using Zustand
 */
export const useMovieExploreStore = create<MovieExploreStore>((set, get) => ({
  // Initial state
  nodes: [],
  vibe: DEFAULT_VIBE,
  currentConstellationId: null,
  savedConstellations: [],

  exploreId: null,
  interpretation: null,
  results: [],
  suggestedNodes: [],
  coherenceScore: 0,

  loading: false,
  error: null,
  cacheHit: false,
  responseTimeMs: 0,

  addNode: (node) => {
    const { nodes } = get();
    const exists = nodes.some((n) => n.type === node.type && n.value === node.value);
    if (!exists) {
      set({ nodes: [...nodes, node] });
    }
  },

  removeNode: (index) => {
    const { nodes } = get();
    set({ nodes: nodes.filter((_, i) => i !== index) });
  },

  updateNodeWeight: (index, weight) => {
    const { nodes } = get();
    const updatedNodes = nodes.map((node, i) =>
      i === index ? { ...node, weight: Math.max(0, Math.min(1, weight)) } : node
    );
    set({ nodes: updatedNodes });
  },

  setVibe: (vibeUpdate) => {
    const { vibe } = get();
    const clampedVibe = Object.entries(vibeUpdate).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: Math.max(0, Math.min(1, value as number)),
      }),
      {}
    );
    set({ vibe: { ...vibe, ...clampedVibe } });
  },

  clearConstellation: () => {
    set({
      nodes: [],
      vibe: DEFAULT_VIBE,
      currentConstellationId: null,
      results: [],
      interpretation: null,
      suggestedNodes: [],
      coherenceScore: 0,
      error: null,
    });
  },

  loadConstellation: (constellation) => {
    set({
      nodes: constellation.nodes,
      vibe: constellation.vibe,
      currentConstellationId: constellation.id,
      results: [],
      interpretation: null,
      error: null,
    });
  },

  setSavedConstellations: (constellations) => {
    set({ savedConstellations: constellations });
  },

  addSavedConstellation: (constellation) => {
    const { savedConstellations } = get();
    set({ savedConstellations: [constellation, ...savedConstellations] });
  },

  deleteSavedConstellation: (id) => {
    const { savedConstellations, currentConstellationId } = get();
    set({
      savedConstellations: savedConstellations.filter((c) => c.id !== id),
      currentConstellationId: currentConstellationId === id ? null : currentConstellationId,
    });
  },

  setExploreResults: (response) => {
    set({
      exploreId: response.data.explore_id,
      interpretation: response.data.interpretation,
      results: response.data.recommendations,
      suggestedNodes: response.data.suggested_nodes,
      coherenceScore: response.data.coherence_score,
      cacheHit: response.data.cache_hit,
      responseTimeMs: response.data.response_time_ms,
      loading: false,
      error: null,
    });
  },

  clearResults: () => {
    set({
      exploreId: null,
      interpretation: null,
      results: [],
      suggestedNodes: [],
      coherenceScore: 0,
      cacheHit: false,
      responseTimeMs: 0,
      error: null,
    });
  },

  setLoading: (loading) => {
    set({ loading });
  },

  setError: (error) => {
    set({ error, loading: false });
  },

  isValid: () => {
    return get().nodes.length >= 3;
  },

  getCoherenceLevel: (): CoherenceLevel => {
    const { coherenceScore } = get();
    if (coherenceScore < 0.4) return 'wild';
    if (coherenceScore > 0.7) return 'tight';
    return 'balanced';
  },
}));
